import apiClient, { PaginatedResponse } from './client';

export interface Server {
  id: string;
  name: string;
  host: string;
  port: number;
  username: string;
  is_local: boolean;
  description: string | null;
  docker_compose_path: string | null;
  created_at: string;
  updated_at: string;
}

export interface ServerCreate {
  name: string;
  host: string;
  port?: number;
  username: string;
  ssh_key: string;
  is_local?: boolean;
  description?: string;
  docker_compose_path?: string;
}

export interface ServerUpdate {
  name?: string;
  host?: string;
  port?: number;
  username?: string;
  ssh_key?: string;
  is_local?: boolean;
  description?: string;
  docker_compose_path?: string;
}

export interface ConnectionTest {
  success: boolean;
  message: string;
}

export interface ServerStats {
  server_id: string;
  uptime: string | null;
  load_average: string | null;
  memory_usage: string | null;
  disk_usage: string | null;
}

export interface ListServersParams {
  page?: number;
  limit?: number;
  search?: string;
}

export interface DockerContainer {
  id: string;
  name: string;
  image: string;
  status: string;
  ports: string;
  created: string;
}

export interface ContainersResponse {
  server_id: string;
  containers: DockerContainer[];
  count: number;
  error: string | null;
}

export interface ContainerLogsResponse {
  server_id: string;
  container_id: string;
  logs: string | null;
  error: string | null;
}

export interface ContainerActionResponse {
  success: boolean;
  message: string;
}

export interface BrowseFilesResponse {
  server_id: string;
  path: string;
  files: string[];
  error: string | null;
}

export interface SetDockerComposePathResponse {
  success: boolean;
  docker_compose_path: string | null;
}

export interface ComposeServicesResponse {
  server_id: string;
  compose_path: string;
  services: string[];
  error: string | null;
}

export interface DirectoryListResponse {
  server_id: string;
  path: string;
  parent: string;
  folders: string[];
  files: string[];
  error: string | null;
}

export interface ReadFileResponse {
  server_id: string;
  path: string;
  content: string | null;
  error: string | null;
}

export interface WriteFileResponse {
  server_id: string;
  path: string;
  success: boolean;
  error: string | null;
}

export interface StaticAppsResponse {
  server_id: string;
  static_path: string;
  apps: string[];
  error: string | null;
}

export interface DiscoveryJobResponse {
  job_id: string;
  server_id: string;
  status: string;
  message: string;
}

export interface DiscoveryJobStatus {
  job_id: string;
  server_id: string;
  status: 'pending' | 'running' | 'success' | 'partial' | 'failed';
  phase?: string;
  progress: number;
  started_at?: string;
  completed_at?: string;
  docker_apps_count: number;
  static_apps_count: number;
  domains_count: number;
  environments_count: number;
  errors: string[];
}

export const serversApi = {
  list: async (params: ListServersParams = {}): Promise<PaginatedResponse<Server>> => {
    const response = await apiClient.get<PaginatedResponse<Server>>('/api/v1/servers', {
      params,
    });
    return response.data;
  },

  get: async (id: string): Promise<Server> => {
    const response = await apiClient.get<Server>(`/api/v1/servers/${id}`);
    return response.data;
  },

  create: async (data: ServerCreate): Promise<Server> => {
    const response = await apiClient.post<Server>('/api/v1/servers', data);
    return response.data;
  },

  update: async (id: string, data: ServerUpdate): Promise<Server> => {
    const response = await apiClient.patch<Server>(`/api/v1/servers/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/servers/${id}`);
  },

  testConnection: async (id: string): Promise<ConnectionTest> => {
    const response = await apiClient.post<ConnectionTest>(`/api/v1/servers/${id}/test`);
    return response.data;
  },

  getStats: async (id: string): Promise<ServerStats> => {
    const response = await apiClient.get<ServerStats>(`/api/v1/servers/${id}/stats`);
    return response.data;
  },

  getContainers: async (id: string): Promise<ContainersResponse> => {
    const response = await apiClient.get<ContainersResponse>(`/api/v1/servers/${id}/containers`);
    return response.data;
  },

  getContainerLogs: async (serverId: string, containerId: string, tail: number = 100): Promise<ContainerLogsResponse> => {
    const response = await apiClient.get<ContainerLogsResponse>(
      `/api/v1/servers/${serverId}/containers/${containerId}/logs`,
      { params: { tail } }
    );
    return response.data;
  },

  containerAction: async (serverId: string, containerId: string, action: 'start' | 'stop' | 'restart'): Promise<ContainerActionResponse> => {
    const response = await apiClient.post<ContainerActionResponse>(
      `/api/v1/servers/${serverId}/containers/${containerId}/${action}`
    );
    return response.data;
  },

  browseFiles: async (serverId: string, path: string = '/', pattern: string = '*docker-compose*.yml'): Promise<BrowseFilesResponse> => {
    const response = await apiClient.get<BrowseFilesResponse>(
      `/api/v1/servers/${serverId}/browse`,
      { params: { path, pattern } }
    );
    return response.data;
  },

  setDockerComposePath: async (serverId: string, path: string | null): Promise<SetDockerComposePathResponse> => {
    const response = await apiClient.post<SetDockerComposePathResponse>(
      `/api/v1/servers/${serverId}/docker-compose-path`,
      { path }
    );
    return response.data;
  },

  getComposeServices: async (serverId: string, composePath: string): Promise<ComposeServicesResponse> => {
    const response = await apiClient.get<ComposeServicesResponse>(
      `/api/v1/servers/${serverId}/compose-services`,
      { params: { path: composePath } }
    );
    return response.data;
  },

  listDirectory: async (serverId: string, path: string = '/home/ubuntu'): Promise<DirectoryListResponse> => {
    const response = await apiClient.get<DirectoryListResponse>(
      `/api/v1/servers/${serverId}/list-directory`,
      { params: { path } }
    );
    return response.data;
  },

  readFile: async (serverId: string, path: string): Promise<ReadFileResponse> => {
    const response = await apiClient.get<ReadFileResponse>(
      `/api/v1/servers/${serverId}/read-file`,
      { params: { path } }
    );
    return response.data;
  },

  writeFile: async (serverId: string, path: string, content: string): Promise<WriteFileResponse> => {
    const response = await apiClient.post<WriteFileResponse>(
      `/api/v1/servers/${serverId}/write-file`,
      { content },
      { params: { path } }
    );
    return response.data;
  },

  getStaticApps: async (serverId: string, path?: string): Promise<StaticAppsResponse> => {
    const response = await apiClient.get<StaticAppsResponse>(
      `/api/v1/servers/${serverId}/static-apps`,
      { params: path ? { path } : {} }
    );
    return response.data;
  },

  triggerDiscovery: async (serverId: string, mode: 'merge' | 'replace' = 'merge'): Promise<DiscoveryJobResponse> => {
    const response = await apiClient.post<DiscoveryJobResponse>(
      `/api/v1/servers/${serverId}/discover`,
      null,
      { params: { mode } }
    );
    return response.data;
  },

  getDiscoveryStatus: async (serverId: string, jobId: string): Promise<DiscoveryJobStatus> => {
    const response = await apiClient.get<DiscoveryJobStatus>(
      `/api/v1/servers/${serverId}/discover/${jobId}`
    );
    return response.data;
  },
};
